import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  listOrderStatusDefinitions,
  createOrderStatusDefinition,
  updateOrderStatusDefinition,
  removeOrderStatusDefinition,
  listOrderTypes,
  createOrderType,
  updateOrderType,
  deleteOrderType,
} from '../app/api';
import { useTranslation } from '../app/LanguageContext';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

const defaultHexColor = '#607d8b';

function SortableCatalogCard({
  definition: d,
  disabled,
  translate,
  defaultHexColor,
  onEdit,
  onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: d.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  return (
    <Card
      ref={setNodeRef}
      style={style}
      variant="outlined"
      sx={isDragging ? { boxShadow: 2 } : undefined}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            size="small"
            disabled={disabled}
            {...attributes}
            {...listeners}
            sx={{ cursor: disabled ? 'default' : 'grab', touchAction: 'none', flexShrink: 0 }}
            aria-label={translate('orders.workflow_catalog_drag_handle')}
            data-testid={`workflow-catalog-drag-handle-${d.id}`}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: d.color || '#ccc', flexShrink: 0 }} />
          <Typography fontWeight={600} sx={{ flex: 1, wordBreak: 'break-word' }}>
            {d.name}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            fullWidth
            variant="outlined"
            startIcon={<EditIcon />}
            disabled={disabled}
            onClick={() => onEdit({ id: d.id, name: d.name, color: d.color || defaultHexColor })}
          >
            {translate('orders.workflow_edit_name')}
          </Button>
          <Button
            size="small"
            fullWidth
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}
            disabled={disabled}
            onClick={onDelete}
          >
            {translate('orders.workflow_delete_status')}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

function SortableCatalogTableRow({
  definition: d,
  disabled,
  translate,
  defaultHexColor,
  onEdit,
  onDelete,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: d.id,
    disabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };
  return (
    <TableRow ref={setNodeRef} style={style} sx={isDragging ? { bgcolor: 'action.hover' } : undefined}>
      <TableCell sx={{ width: 48, py: 0.5 }}>
        <IconButton
          size="small"
          disabled={disabled}
          {...attributes}
          {...listeners}
          sx={{ cursor: disabled ? 'default' : 'grab', touchAction: 'none' }}
          aria-label={translate('orders.workflow_catalog_drag_handle')}
          data-testid={`workflow-catalog-drag-handle-${d.id}`}
        >
          <DragIndicatorIcon fontSize="small" />
        </IconButton>
      </TableCell>
      <TableCell>
        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: d.color || '#ccc' }} />
      </TableCell>
      <TableCell sx={{ wordBreak: 'break-word' }}>{d.name}</TableCell>
      <TableCell align="right">
        <IconButton
          size="small"
          disabled={disabled}
          onClick={() => onEdit({ id: d.id, name: d.name, color: d.color || defaultHexColor })}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" disabled={disabled} onClick={onDelete}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
}

const WORKFLOW_TAB_PATH = { 0: 'catalog', 1: 'types' };

const OrderWorkflowSettingsPage = () => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const { section } = useParams();
  const tab = section === 'types' ? 1 : 0;
  const [definitions, setDefinitions] = useState([]);
  const [types, setTypes] = useState([]);
  const [newStatusName, setNewStatusName] = useState('');
  const [newStatusColor, setNewStatusColor] = useState(defaultHexColor);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeNameError, setNewTypeNameError] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editCatalog, setEditCatalog] = useState(null);
  const [deleteCatalog, setDeleteCatalog] = useState(null);
  const [pendingDeleteOrderType, setPendingDeleteOrderType] = useState(null);
  const [reassignGlobalId, setReassignGlobalId] = useState('');
  const [catalogOrderSaving, setCatalogOrderSaving] = useState(false);

  const catalogSortableIds = useMemo(() => definitions.map((d) => d.id), [definitions]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const loadDefinitions = useCallback(async () => {
    const res = await listOrderStatusDefinitions();
    setDefinitions(res.data || []);
  }, []);

  const loadTypes = useCallback(async () => {
    const res = await listOrderTypes();
    setTypes(res.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadDefinitions();
        await loadTypes();
      } catch (e) {
        setSnackbar({ open: true, message: e.message, severity: 'error' });
      }
    })();
  }, [loadDefinitions, loadTypes]);

  useEffect(() => {
    if (section !== 'catalog' && section !== 'types') {
      navigate('/settings/order-workflow/catalog', { replace: true });
    }
  }, [section, navigate]);


  const persistCatalogOrder = useCallback(
    async (orderedIds) => {
      if (!orderedIds.length) return;
      setCatalogOrderSaving(true);
      try {
        await Promise.all(
          orderedIds.map((id, index) => updateOrderStatusDefinition(id, { catalog_sort: index }))
        );
        await loadDefinitions();
        setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      } catch (e) {
        setSnackbar({ open: true, message: e.message, severity: 'error' });
      } finally {
        setCatalogOrderSaving(false);
      }
    },
    [loadDefinitions, translate]
  );

  const handleCatalogDragEnd = useCallback(
    (event) => {
      if (catalogOrderSaving) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = definitions.findIndex((d) => d.id === active.id);
      const newIndex = definitions.findIndex((d) => d.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      persistCatalogOrder(arrayMove(definitions, oldIndex, newIndex).map((x) => x.id));
    },
    [definitions, persistCatalogOrder, catalogOrderSaving]
  );

  const catalogPanel = (
    <Paper sx={{ p: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        {translate('orders.workflow_status_catalog')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {translate('orders.workflow_catalog_order_hint')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
        <TextField
          fullWidth
          size="small"
          label={translate('orders.workflow_new_status_name')}
          value={newStatusName}
          onChange={(e) => setNewStatusName(e.target.value)}
          inputProps={{ 'data-testid': 'workflow-new-status-name' }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {translate('orders.workflow_status_color')}
          </Typography>
          <TextField
            type="color"
            size="small"
            value={newStatusColor}
            onChange={(e) => setNewStatusColor(e.target.value)}
            sx={{ width: 56, minWidth: 56 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            label="Hex"
            value={newStatusColor}
            onChange={(e) => setNewStatusColor(e.target.value)}
            sx={{ width: 100 }}
            inputProps={{ 'data-testid': 'workflow-new-status-hex' }}
          />
        </Stack>
        <Button
          variant="contained"
          data-testid="workflow-catalog-add-status"
          disabled={catalogOrderSaving}
          onClick={async () => {
            try {
              await createOrderStatusDefinition({
                name: newStatusName,
                catalog_sort: definitions.length,
                color: newStatusColor || '',
              });
              setNewStatusName('');
              setNewStatusColor(defaultHexColor);
              await loadDefinitions();
              setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
            } catch (e) {
              setSnackbar({ open: true, message: e.message, severity: 'error' });
            }
          }}
        >
          {translate('common.add')}
        </Button>
      </Stack>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCatalogDragEnd}>
        {isCompact ? (
          <SortableContext items={catalogSortableIds} strategy={verticalListSortingStrategy}>
            <Stack spacing={1.5}>
              {definitions.map((d) => (
                <SortableCatalogCard
                  key={d.id}
                  definition={d}
                  disabled={catalogOrderSaving}
                  translate={translate}
                  defaultHexColor={defaultHexColor}
                  onEdit={setEditCatalog}
                  onDelete={() => {
                    setReassignGlobalId('');
                    setDeleteCatalog({ id: d.id, name: d.name });
                  }}
                />
              ))}
            </Stack>
          </SortableContext>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 48 }} aria-label={translate('orders.workflow_catalog_drag_column')}>
                    <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled', verticalAlign: 'middle' }} aria-hidden />
                  </TableCell>
                  <TableCell>{translate('orders.workflow_status_color')}</TableCell>
                  <TableCell>{translate('orders.workflow_name')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <SortableContext items={catalogSortableIds} strategy={verticalListSortingStrategy}>
                <TableBody>
                  {definitions.map((d) => (
                    <SortableCatalogTableRow
                      key={d.id}
                      definition={d}
                      disabled={catalogOrderSaving}
                      translate={translate}
                      defaultHexColor={defaultHexColor}
                      onEdit={setEditCatalog}
                      onDelete={() => {
                        setReassignGlobalId('');
                        setDeleteCatalog({ id: d.id, name: d.name });
                      }}
                    />
                  ))}
                </TableBody>
              </SortableContext>
            </Table>
          </TableContainer>
        )}
      </DndContext>
    </Paper>
  );

  const typesPanel = (
    <Stack spacing={2}>
      <Paper sx={{ p: { xs: 1.5, sm: 2 }, overflow: 'hidden' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {translate('orders.workflow_order_types')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={translate('orders.workflow_new_type_name')}
            value={newTypeName}
            onChange={(e) => {
              setNewTypeName(e.target.value);
              setNewTypeNameError(false);
            }}
            error={newTypeNameError}
            helperText={newTypeNameError ? translate('orders.workflow_name_empty') : ''}
            inputProps={{ 'data-testid': 'workflow-new-type-name' }}
          />
          <Button
            variant="outlined"
            data-testid="workflow-add-order-type"
            onClick={async () => {
              if (!String(newTypeName).trim()) {
                setNewTypeNameError(true);
                setSnackbar({ open: true, message: translate('orders.workflow_name_empty'), severity: 'error' });
                return;
              }
              try {
                await createOrderType({ name: newTypeName });
                setNewTypeName('');
                setNewTypeNameError(false);
                await loadTypes();
                setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
              } catch (e) {
                setSnackbar({ open: true, message: e.message, severity: 'error' });
              }
            }}
          >
            {translate('common.add')}
          </Button>
        </Stack>
        {isCompact ? (
          <Stack spacing={1}>
            {types.map((t) => (
              <Card
                key={t.id}
                variant="outlined"
                onClick={() => setSelectedTypeId(t.id)}
                sx={{
                  cursor: 'pointer',
                  borderColor: t.id === selectedTypeId ? 'primary.main' : undefined,
                  borderWidth: t.id === selectedTypeId ? 2 : 1,
                }}
              >
                <CardContent sx={{ py: 1.5 }}>
                  <Typography fontWeight={600}>{t.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t.slug}
                  </Typography>
                  <FormControlLabel
                    sx={{ mt: 0.5, ml: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={t.active !== false}
                        onChange={async (e) => {
                          e.stopPropagation();
                          try {
                            await updateOrderType(t.id, { active: e.target.checked });
                            await loadTypes();
                          } catch (err) {
                            setSnackbar({ open: true, message: err.message, severity: 'error' });
                          }
                        }}
                      />
                    }
                    label={translate('orders.workflow_type_active')}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Button
                    fullWidth
                    size="small"
                    color="primary"
                    variant="outlined"
                    startIcon={<AccountTreeIcon />}
                    sx={{ mt: 1 }}
                    onClick={(e) => { e.stopPropagation(); navigate(`/settings/order-workflow/types/${t.id}/edit`); }}
                  >
                    Editar fluxo
                  </Button>
                  <Button
                    fullWidth
                    size="small"
                    color="error"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    disabled={t.slug === 'general' || t.slug === 'legacy'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPendingDeleteOrderType({ id: t.id, name: t.name });
                    }}
                  >
                    {translate('common.delete')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate('orders.workflow_name')}</TableCell>
                  <TableCell>{translate('orders.workflow_type_slug')}</TableCell>
                  <TableCell align="center">{translate('orders.workflow_type_active')}</TableCell>
                  <TableCell align="right">{translate('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {types.map((t) => (
                  <TableRow key={t.id} selected={t.id === selectedTypeId} hover onClick={() => setSelectedTypeId(t.id)}>
                    <TableCell>{t.name}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all' }}>{t.slug}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        size="small"
                        checked={t.active !== false}
                        onChange={async (e) => {
                          try {
                            await updateOrderType(t.id, { active: e.target.checked });
                            await loadTypes();
                          } catch (err) {
                            setSnackbar({ open: true, message: err.message, severity: 'error' });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Editar fluxo visual">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/settings/order-workflow/types/${t.id}/edit`); }}
                        >
                          <AccountTreeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={t.slug === 'general' || t.slug === 'legacy'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDeleteOrderType({ id: t.id, name: t.name });
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

    </Stack>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 960, mx: 'auto', px: { xs: 0, sm: 1 }, py: { xs: 0.5, sm: 2 }, boxSizing: 'border-box' }}>
      <Tabs
        value={tab}
        onChange={(_, v) => navigate(`/settings/order-workflow/${WORKFLOW_TAB_PATH[v]}`)}
        sx={{ mb: 2 }}
      >
        <Tab label={translate('orders.workflow_tab_catalog')} />
        <Tab label={translate('orders.workflow_tab_types')} />
      </Tabs>

      {tab === 0 && catalogPanel}
      {tab === 1 && typesPanel}

      <Dialog open={Boolean(editCatalog)} onClose={() => setEditCatalog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{translate('orders.workflow_rename_title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('orders.workflow_status_technical_id')}
          </Typography>
          {editCatalog && (
            <>
              <TextField
                margin="dense"
                fullWidth
                label={translate('orders.workflow_name')}
                value={editCatalog.name}
                onChange={(e) => setEditCatalog((s) => ({ ...s, name: e.target.value }))}
                inputProps={{ 'data-testid': 'workflow-edit-status-name' }}
              />
              <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
                <TextField type="color" value={editCatalog.color} onChange={(e) => setEditCatalog((s) => ({ ...s, color: e.target.value }))} sx={{ width: 56 }} />
                <TextField
                  fullWidth
                  label={translate('orders.workflow_status_color')}
                  value={editCatalog.color}
                  onChange={(e) => setEditCatalog((s) => ({ ...s, color: e.target.value }))}
                  inputProps={{ 'data-testid': 'workflow-edit-status-hex' }}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                id: {editCatalog.id}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCatalog(null)}>{translate('common.cancel')}</Button>
          <Button
            variant="contained"
            data-testid="workflow-edit-status-save"
            onClick={async () => {
              if (!editCatalog) return;
              const name = (editCatalog.name || '').trim();
              if (!name) {
                setSnackbar({ open: true, message: translate('orders.workflow_name_empty'), severity: 'error' });
                return;
              }
              try {
                await updateOrderStatusDefinition(editCatalog.id, { name, color: editCatalog.color });
                await loadDefinitions();
                setEditCatalog(null);
                setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
              } catch (e) {
                setSnackbar({ open: true, message: e.message, severity: 'error' });
              }
            }}
          >
            {translate('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteCatalog)} onClose={() => setDeleteCatalog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{translate('orders.workflow_delete_status')}</DialogTitle>
        <DialogContent>
          {deleteCatalog && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{deleteCatalog.name}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {translate('orders.workflow_delete_intro')}
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>{translate('orders.workflow_reassign_to')}</InputLabel>
            <Select
              label={translate('orders.workflow_reassign_to')}
              value={reassignGlobalId}
              onChange={(e) => setReassignGlobalId(e.target.value)}
              data-testid="workflow-delete-status-reassign"
              SelectDisplayProps={{ 'data-testid': 'workflow-delete-status-reassign-trigger' }}
            >
              <MenuItem value="">
                <em>{translate('orders.workflow_reassign_placeholder')}</em>
              </MenuItem>
              {definitions.filter((d) => d.id !== deleteCatalog?.id).map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCatalog(null)}>{translate('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            data-testid="workflow-delete-status-confirm"
            onClick={async () => {
            if (!deleteCatalog) return;
            try {
              await removeOrderStatusDefinition(deleteCatalog.id, reassignGlobalId);
              await loadDefinitions();
              await loadTypes();
              setDeleteCatalog(null);
              setReassignGlobalId('');
              setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
            } catch (e) {
              setSnackbar({ open: true, message: e.message, severity: 'error' });
            }
          }}
          >
            {translate('orders.workflow_delete_status')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDeleteOrderType)} onClose={() => setPendingDeleteOrderType(null)} fullWidth maxWidth="sm">
        <DialogTitle>{translate('orders.workflow_delete_order_type')}</DialogTitle>
        <DialogContent>
          {pendingDeleteOrderType && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{pendingDeleteOrderType.name}</strong>
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {translate('orders.workflow_delete_order_type_intro')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteOrderType(null)}>{translate('common.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!pendingDeleteOrderType) return;
              try {
                await deleteOrderType(pendingDeleteOrderType.id);
                if (selectedTypeId === pendingDeleteOrderType.id) setSelectedTypeId('');
                setPendingDeleteOrderType(null);
                await loadTypes();
                setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
              } catch (err) {
                setSnackbar({ open: true, message: err.message, severity: 'error' });
              }
            }}
          >
            {translate('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderWorkflowSettingsPage;
